import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { Button } from '../Button';
import { StepContainer } from '../StepContainer';
import { IOption } from '../../types';
import { TFormValues } from '../../App';
import { breakpoints, colors, formFields } from '../../constants';
import { createMarkup } from '../../utils';
import { CloseIcon } from '../../svg';

interface IHelperImageThumbnailContainer {
    imageOpen: boolean;
}

const HelperImageThumbnailContainer = styled.div<IHelperImageThumbnailContainer>`
    cursor: ${({ imageOpen }) => (imageOpen ? 'zoom-out' : 'zoom-in')};
`;
const HelperImageThumbnail = styled.img``;
const ThumbnailTitle = styled.h5``;
const HelperImageContainer = styled.div``;
const HelperImage = styled.img``;
const IconHolder = styled.div``;
const Row = styled.div``;
const DividerBox = styled.span``;
const ButtonContainer = styled.div``;

interface IProps {
    className?: string;
    options: IOption[];
    onlineOrder: boolean;
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    setFieldValue(field: keyof TFormValues, value: string): void;
    onStepButtonClick(option: IOption): void;
}

const BaseOrderNumberForm = ({
    className,
    onlineOrder,
    options,
    getFieldError,
    setFieldValue,
    onStepButtonClick,
}: IProps) => {
    const [onlineOrderNumberFieldValue, setOnlineOrderNumberFieldValue] =
        useState('');
    const [storeOrderNumberFirstPart, setStoreOrderNumberFirstPart] =
        useState('');
    const [storeOrderNumberSecondPart, setStoreOrderNumberSecondPart] =
        useState('');
    const [helperImageOpen, setHelperImageOpen] = useState(false);

    // Reconstruct and reset orderNumber if field values change
    useEffect(() => {
        // (e.g. 0000123456 or 1234/1234)
        const orderNumber = onlineOrder
            ? ['0000', onlineOrderNumberFieldValue].join('')
            : [storeOrderNumberFirstPart, '/', storeOrderNumberSecondPart]
                  .join('')
                  .replace(/ /g, '');

        setFieldValue('orderNumber', orderNumber);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        onlineOrderNumberFieldValue,
        storeOrderNumberFirstPart,
        storeOrderNumberSecondPart,
    ]);

    const stepTitle = onlineOrder
        ? 'Heb je het <strong>ordernummer</strong> van je online aankoop nog?'
        : 'Voer je <strong>factuurnummer</strong> in';

    const orderNumberFieldStyles = {
        maxWidth: '175px',
        marginBottom: '20px',
        '.MuiInputBase-input': { fontSize: '18px' },
    };

    const invoiceImageUrl = onlineOrder
        ? 'https://grandvision.s3.amazonaws.com/pnl/images/pnl-contactform-invoice.jpg'
        : 'https://grandvision.s3.amazonaws.com/pnl/images/pnl-contactform-invoice-store.jpg';

    return (
        <StepContainer className={className}>
            <h3 dangerouslySetInnerHTML={createMarkup(stepTitle)} />
            <HelperImageThumbnailContainer
                imageOpen={helperImageOpen}
                onClick={() => setHelperImageOpen(!helperImageOpen)}
            >
                <HelperImageThumbnail
                    alt="Voorbeeld factuur thumbnail"
                    src={invoiceImageUrl}
                />
                <ThumbnailTitle>
                    Waar vind ik mijn{' '}
                    {onlineOrder ? 'ordernummer' : 'factuurnummer'}?
                </ThumbnailTitle>
            </HelperImageThumbnailContainer>
            {helperImageOpen && (
                <HelperImageContainer>
                    <IconHolder
                        onClick={() => setHelperImageOpen(!helperImageOpen)}
                    >
                        <CloseIcon />
                    </IconHolder>
                    <HelperImage
                        alt="Voorbeeld factuur"
                        src={invoiceImageUrl}
                        onClick={() => setHelperImageOpen(!helperImageOpen)}
                    />
                </HelperImageContainer>
            )}
            <Row>
                {onlineOrder ? (
                    <>
                        <DividerBox>0000</DividerBox>
                        <TextField
                            error={!!getFieldError(formFields.orderNumber)}
                            helperText={getFieldError(formFields.orderNumber)}
                            inputProps={{ maxLength: 6 }}
                            label="6 cijfers"
                            sx={orderNumberFieldStyles}
                            type="tel"
                            value={onlineOrderNumberFieldValue}
                            onChange={(e) =>
                                setOnlineOrderNumberFieldValue(e.target.value)
                            }
                        />
                    </>
                ) : (
                    <>
                        <TextField
                            error={!!getFieldError(formFields.orderNumber)}
                            helperText={getFieldError(formFields.orderNumber)}
                            inputProps={{ maxLength: 12 }}
                            label={'Vul cijfers in'}
                            sx={orderNumberFieldStyles}
                            type="tel"
                            value={storeOrderNumberFirstPart}
                            onChange={(e) =>
                                setStoreOrderNumberFirstPart(e.target.value)
                            }
                        />
                        <DividerBox>/</DividerBox>
                        <TextField
                            error={!!getFieldError(formFields.orderNumber)}
                            helperText={getFieldError(formFields.orderNumber)}
                            inputProps={{ maxLength: 12 }}
                            label={'Vul cijfers in'}
                            sx={orderNumberFieldStyles}
                            type="tel"
                            value={storeOrderNumberSecondPart}
                            onChange={(e) =>
                                setStoreOrderNumberSecondPart(e.target.value)
                            }
                        />
                    </>
                )}
            </Row>
            <ButtonContainer>
                {options.map((option) => (
                    <Button
                        key={option.title}
                        secondary={!!option.secondaryButton}
                        onClick={() => onStepButtonClick(option)}
                    >
                        {option.title}
                    </Button>
                ))}
            </ButtonContainer>
        </StepContainer>
    );
};

export const OrderNumberForm = styled(BaseOrderNumberForm)`
    position: relative;

    h3 {
        margin-bottom: 24px;
    }

    ${HelperImageThumbnailContainer} {
        display: flex;
        margin-bottom: 24px;

        @media (${breakpoints.mediumMin}) {
            position: absolute;
            flex-direction: column;
            top: 50%;
            right: 32px;
            width: 120px;
            transform: translateY(-50%);
        }
    }

    ${HelperImageThumbnail} {
        width: 64px;
        margin-right: 12px;
        border-radius: 3px;
        border-top: 2px solid ${colors.greenPrimary};

        @media (${breakpoints.mediumMin}) {
            border-top: 3px solid ${colors.greenPrimary};
            width: 100%;
        }
    }

    ${ThumbnailTitle} {
        color: ${colors.greenMedium};
        font-weight: 400;
        line-height: 1.2;
        margin: 8px 0 0;
        text-align: center;
    }

    ${HelperImageContainer} {
        position: absolute;
        width: 600px;
        max-width: 100vw;
        border-radius: 5px;
        border-top: 4px solid ${colors.greenPrimary};
        box-shadow: 0px 0px 48px -12px rgba(0, 0, 0, 0.38);
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        line-height: 0;
        z-index: 2;
        cursor: zoom-out;
    }

    ${HelperImage} {
        width: 100%;
    }

    ${IconHolder} {
        position: absolute;
        right: 0;
        height: 36px;
        width: 36px;
        padding: 10px;
        cursor: pointer;
    }

    ${CloseIcon} {
        stroke: ${colors.greenMedium};
        height: 100%;
        width: 100%;
    }

    ${Row} {
        display: flex;
        justify-content: center;

        @media (${breakpoints.mediumMin}) {
            justify-content: flex-start;
        }
    }

    ${DividerBox} {
        position: relative;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin: 0 10px;
        height: 56px;
        font-size: 18px;
        top: 3px;
    }

    ${Button} {
        margin-right: 12px;
        margin-top: 8px;
    }
`;
